import React, { useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get } from 'lodash';
import { Card } from 'common/Card';
import { useCurrentEmployeeId, useCurrentEmployeeRoles } from 'common/contexts/CurrentEmployeeContext';
import {
  useCurrentPayerId,
  useCurrentProviderId,
  useIsNetworkLead,
  useIsPayer,
  useIsCBOProvider,
} from 'common/contexts/CurrentProviderContext';
import { Drawer } from 'common/Drawer';
import { Spinner } from 'common/spinners';
import { hasFeatureRole } from 'common/utils/Roles/hasFeatureRole';
import useWorkqueueRowSelector from 'common/Workqueue/hooks/useWorkqueueRowSelector';
import ClientsDrawer from 'src/components/ClientsDrawer';
import { useFind, useFindRecord } from 'src/api/APIHooks';
import { useFeatureFlag } from 'src/common/hooks';
import { apiV1 } from 'src/api/config';
import { BulkActions, InvoiceDetails, InvoicePagination, InvoicesTable } from '../components';
import { FilterSelectors } from '../components/Filter';
import {
  useInvoiceDrawer,
  useInvoiceExports,
  useInvoiceFilters,
  useInvoicePagination,
  useInvoiceTypes,
  useInvoiceSorting,
  usePayerWQInteractiveView,
} from '../hooks';
import getSort from '../utils/getSort';
import AllFiltersDrawer from '../components/Filter/AllFiltersDrawer';
import { INVOICE_STATUS, INVOICE_STATUSES } from '../constants';

export const InvoiceWorkqueue = ({
  showArchived,
  children,
  showDisputed,
  cboStatuses,
  networkLeadStatuses,
  payerStatuses,
  pageName,
  path,
  renderNoInvoicesFound,
  showInvoiceAging,
  cboBulkActions,
  networkLeadBulkActions,
  defaultBulkActions,
  payerBulkActions,
}) => {
  const pays5214BillingForScreenings = !!useFeatureFlag('pays-5214-billing-for-screenings');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [isClientsDrawerOpen, setIsClientsDrawerOpen] = useState(false);
  const [selectedInvoiceClientId, setSelectedInvoiceClientId] = useState(null);
  const isNetworkLead = useIsNetworkLead();
  const isPayer = useIsPayer();
  const currentProviderId = useCurrentProviderId();
  const isCBOProvider = useIsCBOProvider();
  const payerId = useCurrentPayerId();
  const currentEmployeeId = useCurrentEmployeeId();
  const hasPayerInvoicesRole = hasFeatureRole({
    employeeRoles: useCurrentEmployeeRoles(),
    targetRoleKey: 'payer_invoices',
  });

  const { isLoading: isPayerInteractiveLoading, payerWQInteractiveView } = usePayerWQInteractiveView(payerId);

  let availableStatuses = cboStatuses;
  if (isNetworkLead) availableStatuses = networkLeadStatuses;
  if (isPayer) {
    const filterOutReadOnlySpecificStatus = (statuses) => statuses.filter(
      (status) => status !== INVOICE_STATUS.submitted_to_payer,
    );
    availableStatuses = payerWQInteractiveView ? filterOutReadOnlySpecificStatus(payerStatuses) : payerStatuses;
  }

  const paginationProps = useInvoicePagination();
  const sortProps = useInvoiceSorting({ onUpdate: paginationProps.resetPagination });

  let onUpdateFilter = () => {};
  const {
    defaultFilterValues,
    filterSetters,
    filterValues,
    invoicesQuery,
    isFiltered,
    resetFilters,
  } = useInvoiceFilters({
    availableStatuses,
    showArchived,
    showDisputed,
    onUpdate: () => onUpdateFilter(), // hack - must be an anonymous function, not onUpdateFilter passed directly
    // When bulk actions can be refactored, remove the circular dependencies so we don't need to do this
  });

  const { isLoading: isInvoicesLoading, data } = useFind(
    'invoice',
    invoicesQuery,
    {
      include: 'submitter',
      page: {
        number: paginationProps.pageNumber,
        size: paginationProps.pageSize,
      },
      queryConfig: {
        enabled: !!currentProviderId && !isPayerInteractiveLoading && !isDetailDrawerOpen,
        placeholderData: undefined,
      },
      sort: getSort(sortProps),
    },
  );

  const fetchAllInvoices = useCallback(async () => (
    apiV1.query(
      'invoice',
      invoicesQuery,
      {
        page: {
          number: 1,
          size: paginationProps.totalItemCount,
        },
      },
    )
  ), [invoicesQuery, paginationProps]);

  const isLoading = isPayerInteractiveLoading || isInvoicesLoading;

  const invoices = data?.data?.data ?? [];
  const totalItemCount = data?.data?.paging?.total_count ?? 0;
  const totalPageCount = data?.data?.paging?.total_pages ?? 0;

  const exportProps = useInvoiceExports({ currentEmployeeId, fetchAllInvoices });
  const rowSelectorProps = useWorkqueueRowSelector({ pageRows: invoices });

  onUpdateFilter = () => {
    paginationProps.resetPagination();
    exportProps.deselectAll();
    rowSelectorProps.deselectAll();
  };

  let bulkActions = defaultBulkActions;

  if (payerWQInteractiveView && hasPayerInvoicesRole && payerBulkActions) {
    bulkActions = payerBulkActions;
  } else if (isCBOProvider && cboBulkActions) {
    bulkActions = cboBulkActions;
  } else if (isNetworkLead && networkLeadBulkActions) {
    bulkActions = networkLeadBulkActions;
  }

  const drawerProps = useInvoiceDrawer({
    invoices,
    pageNumber: paginationProps.pageNumber,
    pageSize: paginationProps.pageSize,
    setPageNumber: paginationProps.setPageNumber,
    totalPageCount,
    open: isDetailDrawerOpen,
    setOpen: setIsDetailDrawerOpen,
  });

  useEffect(() => {
    if (drawerProps.clickedInvoice) {
      setSelectedInvoiceClientId(drawerProps.clickedInvoice?.client_id);
    }
  }, [drawerProps.clickedInvoice]);

  const { data: clientDataResponse } = useFindRecord('person', selectedInvoiceClientId, {
    queryConfig: {
      placeholderData: undefined,
      enabled: !!selectedInvoiceClientId,
    },
  });

  const clientData = useMemo(() => get(clientDataResponse, 'data.data'), [clientDataResponse]);

  const invoiceTypes = useInvoiceTypes();
  const showInvoiceType = pays5214BillingForScreenings && invoiceTypes.length > 1;

  const handleClientsDrawerToggle = (value) => {
    drawerProps.setOpen(!value);
    setIsClientsDrawerOpen(value);
  };

  if (!availableStatuses) {
    // eslint-disable-next-line no-console
    console.error('no available statuses, do you have the invoices role and the right provider type for this page?');
    return null;
  }

  if (!isEmpty(children)) {
    return React.cloneElement(children, { showInvoiceType });
  }

  return (
    <section className="divide-y divide-solid divide-dark-border-blue" aria-label="Invoices main content">
      <div className="px-3 py-3">
        {/* eslint-disable no-nested-ternary */}
        {isLoading ? (
          <div role="alert"><Spinner /></div>
        ) : (
          !isEmpty(invoices) ? (
            <>
              <Card className="bg-white py-10 pt-30 overflow-x-scroll">
                <h1 className="text-text-blue font-extrabold font-heavy-font text-2xl mb-6 px-8">
                  {`${pageName} Invoices`}
                </h1>
                <div className="flex items-end m-9 space-x-4">
                  <h2 className="sr-only" id="bulk-actions-heading">Bulk Actions</h2>
                  <BulkActions
                    className="mb-2"
                    bulkActions={bulkActions}
                    allInvoiceExportsLoading={exportProps.allInvoicesLoading}
                    deselectAllExports={exportProps.deselectAll}
                    deselectInvoiceExports={exportProps.deselectInvoices}
                    downloadInvoiceExports={exportProps.downloadInvoices}
                    selectAll={exportProps.selectAll}
                    selectInvoiceExports={exportProps.selectInvoices}
                    selectedInvoiceExportIds={exportProps.selectedInvoiceIds}
                    onClose={() => drawerProps.setOpen(false)}
                    currentEmployeeId={currentEmployeeId}
                    payerWQInteractiveView={payerWQInteractiveView}
                    deselectAllWorkqueueInvoices={rowSelectorProps.deselectAll}
                    deselectWorkqueueRow={rowSelectorProps.deselectRows}
                    selectedRows={rowSelectorProps.selectedRows}
                    aria-labelledby="bulk-actions-heading"
                  />
                  <h2 className="sr-only" id="filters-heading">Filters</h2>
                  <FilterSelectors
                    availableStatuses={availableStatuses}
                    filterSetters={filterSetters}
                    filterValues={filterValues}
                    openFilterDrawer={() => setIsFilterDrawerOpen(true)}
                    invoicesQuery={invoicesQuery}
                    invoiceTypes={invoiceTypes}
                    showArchived={showArchived}
                    showDisputed={showDisputed}
                    showInvoiceType={showInvoiceType}
                    aria-labelledby="filters-heading"
                  />
                </div>
                <InvoicesTable
                  invoices={invoices}
                  openDrawer={drawerProps.openDrawer}
                  showInvoiceAging={showInvoiceAging}
                  showInvoiceType={showInvoiceType}
                  toggleWorkqueueSelectedRow={rowSelectorProps.toggleSelectedRow}
                  toggleWorkqueueSelectAllRows={rowSelectorProps.toggleSelectAllRows}
                  selectedRows={rowSelectorProps.selectedRows}
                  deselectExportInvoices={exportProps.deselectInvoices}
                  selectExportInvoices={exportProps.selectInvoices}
                  selectedInvoiceExportIds={exportProps.selectedInvoiceIds}
                  path={path}
                  {...sortProps}
                />
                <InvoicePagination
                  {...paginationProps}
                  totalItemCount={totalItemCount}
                  totalPageCount={totalPageCount}
                />
              </Card>
            </>
          ) : (
            renderNoInvoicesFound({
              isFiltered,
              resetFilters: () => {
                resetFilters();
                onUpdateFilter();
              },
            })
          )
        )}
        {/* eslint-enable no-nested-ternary */}
        <Drawer open={drawerProps.open} setOpen={drawerProps.setOpen}>
          {
            (isLoading || !drawerProps.clickedInvoice) ? (
              <div className="pt-8" role="alert" aria-live="polite"><Spinner /></div>
            ) : (
              <InvoiceDetails
                getNextInvoice={drawerProps.getNextInvoice}
                getPrevInvoice={drawerProps.getPrevInvoice}
                showInvoiceType={showInvoiceType}
                invoice={drawerProps.clickedInvoice}
                isNetworkLead={isNetworkLead}
                onClose={() => drawerProps.setOpen(false)}
                deselectAll={exportProps.deselectAll}
                payerWQInteractiveView={payerWQInteractiveView}
                path={path}
                isClientsDrawerOpen={isClientsDrawerOpen}
                setIsClientsDrawerOpen={handleClientsDrawerToggle}
                clientData={clientData}
              />
            )
          }
        </Drawer>
        {clientData && (
          <ClientsDrawer
            open={isClientsDrawerOpen}
            contact={clientData}
            onClose={() => handleClientsDrawerToggle(false)}
            DrawerComponent={Drawer}
          />
        )}
        <Drawer open={isFilterDrawerOpen} setOpen={setIsFilterDrawerOpen}>
          <AllFiltersDrawer
            availableStatuses={availableStatuses}
            close={() => setIsFilterDrawerOpen(false)}
            defaultFilterValues={defaultFilterValues}
            filterSetters={filterSetters}
            filterValues={filterValues}
            invoicesQuery={invoicesQuery}
            invoiceTypes={invoiceTypes}
            showArchived={showArchived}
            showDisputed={showDisputed}
            showInvoiceType={showInvoiceType}
          />
        </Drawer>
      </div>
    </section>
  );
};

InvoiceWorkqueue.propTypes = {
  cboBulkActions: PropTypes.array,
  networkLeadBulkActions: PropTypes.array,
  payerBulkActions: PropTypes.array,
  defaultBulkActions: PropTypes.array,
  cboStatuses: PropTypes.arrayOf(PropTypes.oneOf(INVOICE_STATUSES)),
  children: PropTypes.node,
  networkLeadStatuses: PropTypes.arrayOf(PropTypes.oneOf(INVOICE_STATUSES)),
  pageName: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  payerStatuses: PropTypes.arrayOf(PropTypes.oneOf(INVOICE_STATUSES)),
  renderNoInvoicesFound: PropTypes.func.isRequired,
  showArchived: PropTypes.oneOf([true, false, null]),
  showDisputed: PropTypes.oneOf([true, false, null]),
  showInvoiceAging: PropTypes.bool,
  showPayerInvoices: PropTypes.bool,
};

InvoiceWorkqueue.defaultProps = {
  defaultBulkActions: [],
  cboBulkActions: undefined,
  networkLeadBulkActions: undefined,
  payerBulkActions: undefined,
  children: undefined,
  cboStatuses: undefined,
  networkLeadStatuses: undefined,
  payerStatuses: undefined,
  showArchived: false,
  showDisputed: false,
  showInvoiceAging: false,
  showPayerInvoices: false,
};

export default InvoiceWorkqueue;
